import { useEffect, useRef, useState } from 'react';
import {
  WORKSPACE_TIMEZONES,
  slugFrom,
  type CreateWorkspaceRequest,
  type WorkspaceSummary,
} from '@forecastify/shared';
import { Button, Dialog, Icon, Select, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { useSession } from '../../session';
import { forgetAvatars } from '../use-avatar';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C42 · Making a second workspace. Name, picture, timezone.                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The mockup draws no such screen** — it carries three workspaces in `WORKSPACES` and renders
 * neither a switcher nor a create flow, so this is one of the 140. `docs/design-system/` decides:
 * one dialog, the same `Field`-and-footer shape every other create dialog in the product uses.
 *
 * ⚠️⚠️ **THREE FIELDS, AND NO SLUG.** The address is derived from the name and suffixed by `0062`
 * if the tenant already has one. A slug field would be a fourth question about a URL nobody will
 * type, asked at the moment somebody is naming a thing.
 *
 * ⚠️⚠️ **THE PICTURE IS UPLOADED AFTER THE WORKSPACE EXISTS, AND IT HAS TO BE.** A file needs a
 * target id, and the workspace has no id until it is created — so the icon is held in memory,
 * posted second, and **a failure there does not undo the workspace**. Somebody who picked an
 * unreadable image gets a workspace with no picture and can try again from settings, which is far
 * better than losing the workspace they just named.
 */

const MAX_ICON_BYTES = 2 * 1024 * 1024;

export function WorkspaceDialog({
  open,
  onClose,
  onCreated,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Handed the new workspace so the caller can switch to it. */
  readonly onCreated: (workspace: WorkspaceSummary) => void;
}) {
  const t = useT();
  const session = useSession();
  const file = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState<string>(WORKSPACE_TIMEZONES[0]);
  const [icon, setIcon] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* ⚠️ Seeded when the dialog OPENS, not on every render — otherwise every keystroke is undone by
     the next paint. The guard `project-roles-section.tsx` uses. */
  useEffect(() => {
    if (!open) return;
    setName('');
    setTimezone(WORKSPACE_TIMEZONES[0]);
    setIcon(null);
    setPreview(null);
    setError(null);
  }, [open]);

  /* ⚠️ Revoked when it is replaced or the dialog closes — a preview is the one object URL in this
     app with a bounded life, unlike `use-avatar`'s deliberately permanent cache. */
  useEffect(() => {
    if (icon === null) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(icon);
    setPreview(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [icon]);

  const slug = slugFrom(name);

  const submit = (): void => {
    if (name.trim() === '') {
      setError(t('workspaces.error.name'));
      return;
    }
    if (slug === '') {
      setError(t('workspaces.error.slug'));
      return;
    }

    void (async () => {
      setBusy(true);
      setError(null);

      const body: CreateWorkspaceRequest = {
        name: name.trim(),
        language: 'hu',
        timezone: timezone as CreateWorkspaceRequest['timezone'],
      };
      const response = await session
        .request('/workspaces', { method: 'POST', body: JSON.stringify(body) })
        .catch(() => null);

      if (response?.ok !== true) {
        setBusy(false);
        setError(t('error.unavailable'));
        return;
      }

      const created = ((await response.json()) as { data: WorkspaceSummary }).data;

      /* ── the picture, second, and never fatal ─────────────────────────────────────────────── */
      if (icon !== null) {
        const form = new FormData();
        form.append('file', icon);
        await session
          .request(`/files?target=workspace_icon&targetId=${created.id}`, {
            method: 'POST',
            body: form,
            /* ⚠️ The NEW workspace, which the session is not in yet — `session.tsx` lets an explicit
               header win for exactly this. Without it the upload would be filed against the
               workspace somebody is leaving. */
            headers: { 'x-workspace-id': created.id },
          })
          .catch(() => null);
        /* ⚠️ Tell the hooks, not just the cache — `use-avatar.ts` records why a module-level
           counter nothing subscribes to changes nothing on screen. */
        forgetAvatars();
      }

      setBusy(false);
      onCreated({ ...created, hasIcon: icon !== null });
    })();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('workspaces.create')}
      description={t('workspaces.createHint')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium">{t('workspaces.name')}</span>
          <TextInput
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
            placeholder={t('workspaces.namePlaceholder')}
          />
          {/* ⚠️ The derived address, shown rather than asked for — so nobody is surprised by the
              URL later, and nobody has to invent one now. */}
          <span className="text-muted-foreground text-[11.5px]">
            {slug === '' ? t('workspaces.slugPending') : t('workspaces.slugPreview', { slug })}
          </span>
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium">{t('workspaces.icon')}</span>
          <div className="flex items-center gap-3">
            <span className="bg-primary text-primary-foreground relative grid size-10 shrink-0 place-items-center overflow-hidden rounded-lg text-[13px] font-semibold">
              {(name.trim() === '' ? '?' : name.trim()).slice(0, 1).toUpperCase()}
              {preview === null ? null : (
                <img src={preview} alt="" className="absolute inset-0 size-full object-cover" />
              )}
            </span>
            <input
              ref={file}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={(event) => {
                const picked = event.target.files?.[0] ?? null;
                if (picked !== null && picked.size > MAX_ICON_BYTES) {
                  setError(t('workspaces.error.iconSize'));
                  return;
                }
                setError(null);
                setIcon(picked);
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                file.current?.click();
              }}
            >
              <Icon name="upload" size={13} />
              {t('workspaces.pickIcon')}
            </Button>
            {icon === null ? null : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIcon(null);
                }}
              >
                {t('common.delete')}
              </Button>
            )}
          </div>
          <span className="text-muted-foreground text-[11.5px]">{t('workspaces.iconHint')}</span>
        </div>

        <label className="flex w-[220px] flex-col gap-1.5">
          <span className="text-[12px] font-medium">{t('workspaces.timezone')}</span>
          <Select
            value={timezone}
            options={WORKSPACE_TIMEZONES.map((zone) => ({ value: zone, label: zone }))}
            onChange={(event) => {
              setTimezone(event.target.value);
            }}
          />
        </label>

        <div className="mt-1 flex items-center gap-2">
          <span className="text-destructive min-w-0 flex-1 text-[11.5px] leading-relaxed">
            {error ?? ''}
          </span>
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" disabled={busy} onClick={submit}>
            {busy ? t('common.saving') : t('workspaces.create')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
